import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import { Suspense, lazy, useCallback, useMemo } from "react";
import { readField, writeField } from "../utils/objectUtilities";
import { FloatingLabel } from "./FloatingLabel";
import { Spinner } from "react-bootstrap";
import { materializeValue } from "../utils/lazyValue";
import ImageInput from "./ImageInput";

const processRead = (obj, field) => {
    if (!field || Object.keys(field).length === 0) {
        return { value: obj };
    } else if (typeof field === 'string') {
        return { value: readField(obj, field) };
    } else if (Array.isArray(field)) {
        return field.reduce((value, f) => { 
            if (typeof f === 'string') {
                writeField(value, f, readField(obj, f))
            } else {
                writeField(value, f.dest, readField(obj, f.src));
            }
            return value;
        }, {});
    } else {
        return { value: Object.entries(field).reduce((value, [ dest, src ]) => {
            writeField(value, dest, readField(obj, src));
            return value;
        }, {}) };
    }
}

const processWrite = (value, field) => {
    if (!field) {
        return undefined;
    } else if (typeof field === 'string') {
        const delta = {};
        writeField(delta, field, value);
        return delta;
    } else if (Array.isArray(field)) {
        return field.reduce((obj, f) => {
            if (typeof f === 'string') {
                writeField(value, f, readField(obj, f));
            } else {
                writeField(value, f.src, readField(obj, f.dest));
            }
            return value;
        }, {});
    } else {
        return Object.entries(field).reduce((obj, [ dest, src ]) => {
            writeField(obj, src, readField(value, dest));
            return obj;
        }, {});
    }
}

const createCtrlRegistry = () => {
    const coreRegistrations = {
        'object': {
            load: () => Promise.resolve({ toString: ({ value, def, defaultValue }) => value?.data || def.data ? '<object>' : defaultValue }),
            render: (_, { value, def, placeholder, hideLabel, ...props }) => <FloatingLabel value={value?.data ?? def.data} placeholder={placeholder}><object type={value?.mime ?? def.mime} data={value?.data ?? def.data} style={{ width: '100%', height: '100%', ...def.style }} {...props}>{placeholder}</object></FloatingLabel>,
        },
        'iframe': {
            load: () => Promise.resolve({ toString: ({ value, def, defaultValue }) => (value || def.data) ? '<iframe>' : defaultValue }),
            render: (_, { value, def, placeholder, hideLabel, ...props }) => <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} value={value?.data ?? def.data}><iframe title={placeholder} src={value?.data ?? def.data} style={{ width: '100%', height: '100%', ...def.style }} {...props}></iframe></FloatingLabel>,
        },
        'link': {
            load: () => import('./HyperLink'),
            Component: lazy(() => import('./HyperLink')),
            render: (HyperLink, { def, placeholder, hideLabel, ...props }) => {
                return <HyperLink href={def.href} {...props}>{def.text}</HyperLink>
            }
        },
        'static_image': {
            load: () => Promise.resolve({ toString: () => '<static_image>' }),
            render: (_, { def, placeholder, hideLabel }) => {
                return <ImageInput placeholder={placeholder} hideLabel={hideLabel} disabled defaultImage={def.uri} />
            }
        }
    };
    const registry = { ...coreRegistrations };

    const register = (kind, load, render, { defaultPrintValue = () => ' ' } = {}) => {
        if (!render) {
            throw new Error("A render method is required to register custom control " + kind);
        }
        if (registry[kind]) {
            console.warn(`Component type "${kind}" is already registered.`);
            return;
        }
        registry[kind] = { Component: lazy(load), load, render, defaultPrintValue };
    }

    const render = (kind, ...props) => {
        if (!registry[kind]) {
            console.warn(`There is no registered custom component of type "${kind}".`);
            return;
        }
        const { Component, render } = registry[kind];
        return <Suspense fallback={<Spinner></Spinner>}>
            { render(Component, ...props) }
        </Suspense>
    }

    const toString = async (kind, props) => {
        if (!registry[kind]) {
            console.error("Unknown control kind " + kind + " in custom control toString")
        }
        const module = await registry[kind].load();
        if (Object.hasOwn(module, 'toString')) {
            return await module?.toString(props);
        }
        return (await materializeValue(props?.value))?.toString?.() ?? props?.defaultValue;
    }

    const persist = async (kind, props) => {
        if (!registry[kind]) {
            console.error("Unknown control kind " + kind + " in custom control persist")
        }
        if (!props?.def?.persist === true || !props?.def?.field) {
            return undefined;
        }
        const module = await registry[kind].load();
        let value = props?.value;
        if (Object.hasOwn(module, 'persist')) {
            value = await module?.persist(props);
        }
        return processWrite(value, props?.def?.field);
    }

    const defaultPrintValue = (kind) => {
        return registry[kind].defaultPrintValue();
    }

    const clear = () => {
        for (const kind of Object.getOwnPropertyNames(registry)) {
            delete registry[kind];
        }
        for (const kind of Object.getOwnPropertyNames(coreRegistrations)) {
            registry[kind] = coreRegistrations[kind];
        }
    }

    return { register, render, toString, persist, defaultPrintValue, clear };
}

export const ctrlRegistry = createCtrlRegistry();

export const CustomCtrl = ({ def, value: obj, args, disabled = false, readonly = false, restricted = false, required = false, hideLabel = false, live = false, onChange, ...props }) => {
    const value = useMemo(() => processRead(obj, def.field), [obj, def.field]);
    const clientConfig = useClientConfig();
    const { t, i18n } = useTranslation();
    
    const isDisabled = disabled || def.readonly || (restricted && def.restricted);
    const isReadonly = readonly || def.readonly;
    const isLive = live || def.live;
    const displayLabel = def.label ? def.label : '';

    const handleChange = useCallback((value, field) => {
        
        if (field && def.field !== field && def.field) {
            if (Array.isArray(def.field)) {
                field = def.field.find(f => f.dest === field)?.src ?? field;
            } else if (typeof def.field === 'object') {
                field = def.field[field] ?? field;
            }
        } else if (!field && def.field) {
            if (Array.isArray(def.field)) {
                field = def.field.find(f => f.dest === 'value')?.src ?? field;
            } else if (typeof def.field === 'object') {
                field = def.field.value ?? field;
            } 
        }
        
        onChange(value, field);
    }, [def.field, onChange])

    const href = useMemo(() => {
        if (typeof def.href === 'string') {
            return def.href;
        } else if (typeof def.href === 'function') {
            return def.href(obj);
        }
    }, [obj, def])

    const extendedProps = useMemo(() => ({ def, ...value, disabled: isDisabled, readonly: isReadonly, restricted, required, live: isLive, placeholder: displayLabel, hideLabel, onChange: handleChange, href, ...props }), [value, isDisabled, isReadonly, isLive, displayLabel, hideLabel, handleChange, props, restricted, required, def, href]);
    const context = useMemo(() => {
        const context = { clientConfig, t, language: i18n.resolvedLanguage };
        if (args) {
            context.args = typeof args === 'function' ? args(def, obj) : args;
        }
        return context;
    }, [def, obj, args, clientConfig, t, i18n.resolvedLanguage])

    return ctrlRegistry.render(def.type, extendedProps, context);
}

export const customFieldToString = async (def, obj, realizationContext, defaultValue = '', language = 'en', args = undefined) => {
    return ctrlRegistry.toString(def.type, { def, ...processRead(obj, def.field), defaultValue, ...realizationContext, language, args });
}

export const customFieldPersist = async (def, obj) => {
    return await ctrlRegistry.persist(def.type, { def, ...processRead(obj, def.field) });
}

export const defaultPrintValue = def => ctrlRegistry.defaultPrintValue(def.type);

export default CustomCtrl;